import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { Button } from "@/components/ui/Button";
import { toast } from "sonner";
import AdminDashboard from "@/pages/employee/AdminDashboard";
import {
  Loader2, Plane, Receipt, Clock, ArrowUpRight, Plus, Building2,
  FileText, KeyRound, ChevronRight, ChevronLeft, Bell, PlayCircle, ArrowRight,
} from "lucide-react";

const TUTORIAL_VIDEOS = [
  {
    title: "Ghid pornire portal",
    description: "Cum navighezi meniul, cum accesezi pontajul și cererile de concediu.",
    url: "https://embed.app.guidde.com/playbooks/ouaPFQEEWjJem1u2f9vodJ?mode=videoAndDoc",
  },
  {
    title: "Cereri de concediu în portal",
    description: "Cum trimiți și urmărești cererile de concediu, pas cu pas.",
    url: "https://embed.app.guidde.com/playbooks/jQrufUrzvzmiD9GUdjYA4e?mode=videoAndDoc",
  },
];

const RO_MONTHS = ["ian","feb","mar","apr","mai","iun","iul","aug","sep","oct","nov","dec"];

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  if (isNaN(d)) return "";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}


export default function StaffDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tutIdx, setTutIdx] = useState(0);
  const [data, setData] = useState({
    balance: null,
    upcomingTimeOff: [],
    pendingTimeOff: [],
    expensesTotal: 0,
    expensesPending: 0,
    expensesPaid: 0,
    hoursThisMonth: 0,
    lastEntry: null,
    projects: [],
    notifications: [],
    docsNeedingReceipt: 0,
    accessRequestsPending: 0,
  });

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (isAdmin) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        const now = new Date();

        const [balR, reqR, expR, tsR, projR, notifR, docR, accR] = await Promise.all([
          api.get("/timeoff/balances/me").catch(() => ({ data: { balances: [] } })),
          api.get("/timeoff/requests/mine").catch(() => ({ data: [] })),
          api.get("/expenses/reports/mine").catch(() => ({ data: [] })),
          api.get(`/staff/timesheet/my?month=${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`).catch(() => ({ data: [] })),
          api.get("/staff/timesheet/my/projects").catch(() => ({ data: [] })),
          api.get("/notifications?limit=5").catch(() => ({ data: [] })),
          api.get("/personal-docs/mine").catch(() => ({ data: [] })),
          api.get("/access-requests/mine").catch(() => ({ data: [] })),
        ]);

        // Time off
        const annualBal = balR.data.balances?.find(b => b.type?.code === "annual");
        const upcoming = reqR.data
          .filter(r => r.status === "approved" && new Date(r.start_date) >= new Date())
          .sort((a, b) => a.start_date.localeCompare(b.start_date))
          .slice(0, 3);
        const pending = reqR.data
          .filter(r => ["pending_manager", "pending_hr"].includes(r.status));

        // Expenses
        const expensesTotal = expR.data.reduce((s, r) => s + (r.totals?.requested_ron || 0), 0);
        const expensesPending = expR.data.filter(r =>
          ["submitted", "info_requested", "manager_approved", "finance_verified", "approved_for_payment"].includes(r.status)).length;
        const expensesPaid = expR.data.reduce((s, r) => s + (r.totals?.paid_ron || 0), 0);

        // Pontaj
        const hoursThisMonth = (tsR.data || []).reduce((s, e) => s + (e.hours || 0), 0);
        const lastEntry = (tsR.data || []).sort((a, b) => (b.date || "").localeCompare(a.date || ""))[0];

        // Docs
        const docsNeedingReceipt = (docR.data || []).filter(d => d.requires_read_receipt && !d.read_by_viewer).length;

        // Access requests
        const accessPending = (accR.data || []).filter(r => ["submitted", "manager_approved"].includes(r.status)).length;

        setData({
          balance: annualBal,
          upcomingTimeOff: upcoming,
          pendingTimeOff: pending,
          expensesTotal,
          expensesPending,
          expensesPaid,
          hoursThisMonth,
          lastEntry,
          projects: projR.data.slice(0, 4),
          notifications: notifR.data.slice(0, 5),
          docsNeedingReceipt,
          accessRequestsPending: accessPending,
        });
      } catch (e) { toast.error(apiErr(e)); }
      finally { setLoading(false); }
    })();
  }, [isAdmin]);

  if (isAdmin) return <AdminDashboard />;

  const firstName = (user?.name || "").split(" ")[0] || "";
  const now = new Date();
  const greeting = now.getHours() < 12 ? "Bună dimineața" : now.getHours() < 18 ? "Bună ziua" : "Bună seara";

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1200px] mx-auto">
        {/* Greeting */}
        <header className="pb-6 pt-1">
          <div className="text-[12px] font-medium text-slate-400 mb-1">
            {new Date().toLocaleDateString("ro-RO", { weekday: "long", day: "numeric", month: "long" })}
          </div>
          <h1 className="text-[26px] leading-tight font-display font-bold tracking-[-0.01em] text-slate-900">
            {greeting}, {firstName}.
          </h1>
        </header>

        {/* Alerts row */}
        {(data.docsNeedingReceipt > 0 || data.pendingTimeOff.length > 0 || data.expensesPending > 0) && (
          <div className="mb-6 flex flex-wrap gap-2">
            {data.docsNeedingReceipt > 0 && (
              <AlertChip icon={FileText} label={`${data.docsNeedingReceipt} document${data.docsNeedingReceipt > 1 ? "e" : ""} de confirmat`}
                onClick={() => navigate("/staff/documente")} />
            )}
            {data.pendingTimeOff.length > 0 && (
              <AlertChip icon={Plane} label={data.pendingTimeOff.length === 1
                ? "O cerere de concediu este în așteptare"
                : `${data.pendingTimeOff.length} cereri de concediu sunt în așteptare`}
                onClick={() => navigate("/staff/timeoff")} />
            )}
            {data.accessRequestsPending > 0 && (
              <AlertChip icon={KeyRound} label={data.accessRequestsPending === 1
                ? "O cerere de acces este în analiză"
                : `${data.accessRequestsPending} cereri de acces sunt în analiză`}
                onClick={() => navigate("/staff/proiecte-acces")} />
            )}
          </div>
        )}

        {loading ? (
          <div className="py-24 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-ink-muted" />
          </div>
        ) : (
          <>
            {/* Three main summary cards — vivid gradient glass */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 pt-4 mb-9">
              <SummaryCard
                icon={Plane}
                label="Concedii & absențe"
                badge="Sold curent"
                metric={data.balance ? `${data.balance.remaining}` : "—"}
                metricSuffix={data.balance ? "zile rămase" : ""}
                sub={data.upcomingTimeOff[0]
                  ? `Următor: ${fmtDate(data.upcomingTimeOff[0].start_date)} — ${data.upcomingTimeOff[0].type?.name}`
                  : "Fără concediu planificat"}
                cta="Cerere nouă"
                onCta={() => navigate("/staff/timeoff")}
              />
              <SummaryCard
                icon={Receipt}
                label="Deconturi"
                badge="Total plătit"
                metric={`${data.expensesPaid.toLocaleString("ro-RO", { maximumFractionDigits: 0 })}`}
                metricSuffix="RON plătite"
                sub={data.expensesPending > 0
                  ? `${data.expensesPending} decont${data.expensesPending !== 1 ? "uri" : ""} în proces`
                  : "Fără deconturi active"}
                cta="Decont nou"
                onCta={() => navigate("/staff/decontari")}
              />
              <SummaryCard
                icon={Clock}
                label="Pontaj"
                badge={`Luna ${RO_MONTHS[new Date().getMonth()]}`}
                metric={`${data.hoursThisMonth.toFixed(1)}`}
                metricSuffix="ore înregistrate"
                sub={data.lastEntry
                  ? `Ultima intrare: ${fmtDate(data.lastEntry.date)}`
                  : "Nicio intrare luna aceasta"}
                cta="Adaugă pontaj"
                onCta={() => navigate("/staff/pontaj")}
                bars
              />
            </div>

            {/* Two-column bottom section */}
            <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8">
              {/* Tutorial — carusel cu săgeți */}
              <section>
                <div className="flex items-baseline justify-between mb-6">
                  <div>
                    <div className="section-eyebrow">Ghiduri video</div>
                    <div className="section-title">Tutoriale</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="tutorial-prev"
                      onClick={() => setTutIdx(i => (i - 1 + TUTORIAL_VIDEOS.length) % TUTORIAL_VIDEOS.length)}
                      disabled={TUTORIAL_VIDEOS.length < 2}
                      className="h-8 w-8 rounded-full border border-slate-200 bg-white/70 backdrop-blur hover:border-indigo-400 hover:bg-indigo-50/60 flex items-center justify-center transition-colors disabled:opacity-40">
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span data-testid="tutorial-counter"
                      className="inline-flex items-center h-8 px-3 rounded-full text-[12px] font-bold text-white bg-gradient-to-r from-[#3b8ff0] to-[#6f6bf5] shadow-[0_6px_14px_-6px_rgba(96,110,240,0.6)] tabular-nums">
                      {tutIdx + 1}/{TUTORIAL_VIDEOS.length}
                    </span>
                    <button
                      data-testid="tutorial-next"
                      onClick={() => setTutIdx(i => (i + 1) % TUTORIAL_VIDEOS.length)}
                      disabled={TUTORIAL_VIDEOS.length < 2}
                      className="h-8 w-8 rounded-full border border-slate-200 bg-white/70 backdrop-blur hover:border-indigo-400 hover:bg-indigo-50/60 flex items-center justify-center transition-colors disabled:opacity-40">
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="surface overflow-hidden">
                  {(() => {
                    const v = TUTORIAL_VIDEOS[tutIdx];
                    return (
                      <div data-testid={`tutorial-video-${tutIdx}`}>
                        <div className="p-5">
                          <div className="flex items-start gap-3 mb-3">
                            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#3b8ff0] to-[#9b6bf0] text-white flex items-center justify-center shrink-0 shadow-[0_8px_18px_-8px_rgba(96,110,240,0.65)]">
                              <PlayCircle className="h-5 w-5" />
                            </div>
                            <div>
                              <div className="text-[15px] font-semibold text-ink leading-tight">{v.title}</div>
                              {v.description && (
                                <div className="text-[13px] text-ink-muted mt-1">{v.description}</div>
                              )}
                            </div>
                          </div>
                          <div className="relative w-full rounded-xl overflow-hidden bg-neutral-100" style={{ paddingTop: "56.25%" }}>
                            <iframe
                              key={v.url}
                              src={v.url}
                              title={v.title}
                              allow="fullscreen"
                              allowFullScreen
                              frameBorder="0"
                              className="absolute inset-0 w-full h-full"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </section>

              {/* Notifications */}
              <section>
                <div className="flex items-baseline justify-between mb-6">
                  <div>
                    <div className="section-eyebrow">Ultimele actualizări</div>
                    <div className="section-title">Notificări</div>
                  </div>
                  <Link to="/portal/notificari"
                    className="text-[14px] font-medium text-ink-muted hover:text-ink inline-flex items-center gap-1">
                    Toate <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
                {data.notifications.length === 0 ? (
                  <div className="py-12 text-center text-ink-muted border-t border-neutral-200">
                    <Bell className="h-6 w-6 mx-auto text-neutral-300 mb-2" />
                    <div className="text-[13px]">Nicio notificare nouă.</div>
                  </div>
                ) : (
                  <div className="border-t border-neutral-200">
                    {data.notifications.map(n => (
                      <div key={n.id} className="py-4 border-b border-neutral-100">
                        <div className="flex items-start gap-3">
                          {!n.read && <div className="h-1.5 w-1.5 rounded-full bg-ink mt-2 shrink-0" />}
                          <div className={`flex-1 min-w-0 ${n.read ? "text-ink-muted" : "text-ink"}`}>
                            <div className="text-[13px] leading-relaxed">{n.message}</div>
                            <div className="text-[11px] text-ink-muted mt-1 tabular-nums">
                              {`${fmtDate(n.created_at)}, ${new Date(n.created_at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}`}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </PortalLayout>
  );
}


function SummaryCard({ icon: Icon, label, badge, metric, metricSuffix, sub, cta, onCta, bars }) {
  const slug = label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and");
  return (
    <div className="surface surface-interactive p-6 flex flex-col min-h-[220px] relative" data-testid={`summary-card-${slug}`}>
      <div className="absolute -top-4 -right-2 z-10 h-12 w-12 rounded-full bg-gradient-to-br from-[#3b8ff0] to-[#9b6bf0] text-white flex items-center justify-center ring-1 ring-white/70 shadow-[0_10px_22px_-8px_rgba(96,110,240,0.6)]">
        <Icon className="h-5 w-5" />
      </div>
      <div className="text-[16px] font-display font-bold text-slate-900 pr-10">{label}</div>
      {badge && (
        <div className="mt-2 mb-4">
          <span className="pio-badge pio-badge-blue">{badge}</span>
        </div>
      )}
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[36px] font-display font-bold tracking-[-0.01em] tabular-nums text-slate-900 leading-none">
            {metric}
          </div>
          {metricSuffix && (
            <div className="text-[13px] text-slate-500 mt-1.5">{metricSuffix}</div>
          )}
        </div>
        {bars && (
          <div className="flex items-end gap-1.5 h-16" aria-hidden="true">
            {[30, 52, 38, 64, 46, 78, 58, 92].map((h, i) => (
              <span key={i} className="w-[9px] rounded-t-md bg-gradient-to-b from-[#6f6bf5] to-[#3b8ff0] opacity-60"
                style={{ height: `${h}%` }} />
            ))}
          </div>
        )}
      </div>
      <div className="text-[12px] text-slate-500 mt-3 min-h-[18px]">{sub}</div>
      <div className="mt-auto pt-4 border-t border-slate-200/70">
        <button onClick={onCta} className="pio-btn-ghost !h-[38px] px-[18px]" data-testid={`summary-cta-${slug}`}>
          {cta}
          <ArrowRight />
        </button>
      </div>
    </div>
  );
}


function AlertChip({ icon: Icon, label, onClick }) {
  return (
    <button onClick={onClick}
      className="inline-flex items-center gap-2 px-4 h-9 rounded-full text-[13px] font-semibold text-white bg-gradient-to-r from-[#3b8ff0] to-[#6f6bf5] shadow-[0_8px_18px_-8px_rgba(96,110,240,0.65)] hover:-translate-y-px transition-transform">
      <Icon className="h-3.5 w-3.5" />
      {label}
      <ArrowRight className="h-3.5 w-3.5 opacity-80" />
    </button>
  );
}
